import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DollarSign, FileText, Loader2, Circle, Clock, CheckCircle2, AlertCircle, XCircle, ChevronRight, Eye, Paperclip, Search, User, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import SellerThankYouEmailDialog from './SellerThankYouEmailDialog';
import BuyerThankYouEmailDialog from './BuyerThankYouEmailDialog';
import EmailSendHistory from '../shared/EmailSendHistory';
import { statusIcons as saleStatusIcons, statusColors as saleStatusColors } from './saleConstants';

const PostSaleTab = ({ opportunityId, opportunity, sale, isReadOnly, onRefresh }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [buyerSearch, setBuyerSearch] = useState('');
  const [buyerResults, setBuyerResults] = useState([]);
  const [buyerFocused, setBuyerFocused] = useState(false);
  const [quickCreateBuyer, setQuickCreateBuyer] = useState(false);
  const [newBuyer, setNewBuyer] = useState({ crm_type: 'person', first_name: '', last_name: '', org_name: '', industry_role: '', crm_class: 'contact' });
  const [savingBuyer, setSavingBuyer] = useState(false);
  const [updatingOwnership, setUpdatingOwnership] = useState(false);
  const [ownershipUpdated, setOwnershipUpdated] = useState(false);
  const [sellerThankYouDialog, setSellerThankYouDialog] = useState(null);
  const [buyerThankYouDialog, setBuyerThankYouDialog] = useState(null);

  const { data: postSaleData, isLoading: loading } = useQuery({
    queryKey: QK.postSale(opportunityId),
    queryFn: async () => {
      const [delRes, contactsRes] = await Promise.all([
        api.get(`/post-sale?sale_opportunity_id=${opportunityId}`),
        api.get('/crm-entities'),
      ]);
      const contacts = contactsRes.data || [];
      const buyerIds = opportunity?.actual_buyer_crm_entity_ids || [];
      const legacyId = opportunity?.actual_buyer_crm_entity_id;
      const allIds = buyerIds.length > 0 ? buyerIds : (legacyId ? [legacyId] : []);
      const loaded = allIds.map(id => contacts.find(e => e.id === id)).filter(Boolean);
      let buyerPercents = {};
      if (loaded.length > 1) {
        const even = Math.floor(100 / loaded.length);
        loaded.forEach((b, i) => { buyerPercents[b.id] = i === 0 ? 100 - even * (loaded.length - 1) : even; });
      } else if (loaded.length === 1) {
        buyerPercents = { [loaded[0].id]: 100 };
      }
      return { deliverables: delRes.data || [], allContacts: contacts, buyerEntities: loaded, buyerPercents };
    },
    enabled: !!opportunityId,
  });

  const deliverables  = postSaleData?.deliverables  ?? [];
  const allContacts   = postSaleData?.allContacts   ?? [];
  const [buyerEntities, setBuyerEntities] = useState([]);
  const [buyerPercents, setBuyerPercents] = useState({});

  // Sync buyer entities from query data
  useEffect(() => {
    if (postSaleData) {
      setBuyerEntities(postSaleData.buyerEntities);
      setBuyerPercents(postSaleData.buyerPercents);
    }
  }, [postSaleData]);

  const invalidatePostSale = () => qc.invalidateQueries({ queryKey: QK.postSale(opportunityId) });

  const updateDeliverable = async (id, data) => {
    try {
      await api.put(`/post-sale/${id}`, data);
      invalidatePostSale();
    } catch { toast.error('Failed to update'); }
  };

  const handleGenerateInvoice = async () => {
    try {
      // First get sale data to pre-populate
      const saleData = await api.get(`/invoices/generate-from-sale/${opportunityId}`);
      const data = saleData.data;

      const ownerId = data.owner_crm_entity_ids?.[0];
      if (!ownerId) {
        toast.error('No owner linked to this opportunity. Please add an owner before generating an invoice.');
        return;
      }

      // Check CRM entity financial details — owner may be a tenant entity (not in crm_entities)
      let entity = null;
      try {
        const entityRes = await api.get(`/crm-entities/${ownerId}`);
        entity = entityRes.data;
      } catch {
        // Owner ID is not a CRM entity (e.g. it's the tenant itself) — proceed with defaults
      }

      if (entity) {
        const missing = [];
        if (!entity.commission_rate_percent && entity.commission_rate_percent !== 0) missing.push('Commission Rate');
        if (!entity.commission_currency) missing.push('Commission Currency');

        if (missing.length > 0) {
          toast.error(`Incomplete financial details for ${entity.system_name}: ${missing.join(', ')}. Redirecting to their profile...`, { duration: 4000 });
          setTimeout(() => navigate(`/crm/accounts/${ownerId}`), 2000);
          return;
        }
      }

      // Create the invoice using the invoices system
      const invoicePayload = {
        crm_entity_id: ownerId,
        horse_id: data.horse_id,
        sale_opportunity_id: data.sale_opportunity_id,
        entries: data.entries || [],
        add_sale_proceeds: true,
        sale_net_proceeds: data.net_proceeds || 0,
        currency: entity?.commission_currency || 'USD',
        other_notes_terms: `${data.sale_company} - ${data.sale_name} - Hip #${data.hip_number || 'TBD'} - ${data.horse_name}`,
      };
      
      const res = await api.post('/invoices', invoicePayload);

      // Mark the invoice_generation deliverable as complete and store the invoice ID
      const invoiceDeliverable = deliverables.find(d => d.type === 'invoice_generation');
      if (invoiceDeliverable) {
        try {
          await api.put(`/post-sale/${invoiceDeliverable.id}`, {
            status: 'in_progress',
            metadata: { ...(invoiceDeliverable.metadata || {}), invoice_id: res.data.id },
          });
          // Invalidate the query so the button switches to "View Invoice" right away
          invalidatePostSale();
        } catch { /* non-critical — invoice was still created */ }
      }

      toast.success('Invoice created — redirecting to invoice');
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate invoice');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const financial = deliverables.filter(d => d.category === 'financial');
  const handover = deliverables.filter(d => d.category === 'handover');

  const searchBuyer = (query) => {
    if (query.length < 1) { setBuyerResults([]); return; }
    const q = query.toLowerCase();
    const existingIds = new Set(buyerEntities.map(e => e.id));
    setBuyerResults(allContacts.filter(e => e.system_name?.toLowerCase().includes(q) && !existingIds.has(e.id)).slice(0, 8));
  };

  const saveBuyers = async (entities) => {
    try {
      const ids = entities.map(e => e.id);
      const names = entities.map(e => e.system_name).join(', ');
      await api.put(`/sale-opportunities/${opportunityId}`, { 
        actual_buyer_crm_entity_ids: ids,
        actual_buyer_crm_entity_id: ids[0] || null,
        signed_ticket_as: names
      });
      toast.success('Buyer(s) saved');
    } catch { toast.error('Failed to save buyer'); }
  };

  const selectBuyer = async (entity) => {
    const updated = [...buyerEntities, entity];
    setBuyerEntities(updated);
    setBuyerSearch('');
    setBuyerResults([]);
    // Auto-split evenly when adding
    const even = Math.floor(100 / updated.length);
    const newPercents = {};
    updated.forEach((b, i) => { newPercents[b.id] = i === 0 ? 100 - even * (updated.length - 1) : even; });
    setBuyerPercents(newPercents);
    await saveBuyers(updated);
  };

  const removeBuyer = async (entityId) => {
    const updated = buyerEntities.filter(e => e.id !== entityId);
    setBuyerEntities(updated);
    const newPercents = { ...buyerPercents };
    delete newPercents[entityId];
    if (updated.length === 1) newPercents[updated[0].id] = 100;
    setBuyerPercents(newPercents);
    await saveBuyers(updated);
  };

  const updateBuyerPercent = (buyerId, value) => {
    setBuyerPercents(prev => ({ ...prev, [buyerId]: Number(value) || 0 }));
  };

  const handleCreateBuyer = async () => {
    setSavingBuyer(true);
    try {
      const payload = newBuyer.crm_type === 'person'
        ? { crm_type: 'person', crm_class: newBuyer.crm_class, person_first_name: newBuyer.first_name, person_last_name: newBuyer.last_name, person_industry_role: newBuyer.industry_role, system_name: `${newBuyer.first_name} ${newBuyer.last_name}` }
        : { crm_type: 'organization', crm_class: newBuyer.crm_class, organization_name: newBuyer.org_name, organization_industry_role: newBuyer.industry_role, system_name: newBuyer.org_name };
      const res = await api.post('/crm-entities', payload);
      // Invalidate query so new contact appears in allContacts
      invalidatePostSale();
      await selectBuyer(res.data);
      setQuickCreateBuyer(false);
      setNewBuyer({ crm_type: 'person', first_name: '', last_name: '', org_name: '', industry_role: '', crm_class: 'contact' });
      toast.success('Buyer created & added');
    } catch { toast.error('Failed to create'); }
    setSavingBuyer(false);
  };

  const handleUpdateOwnership = async () => {
    if (!opportunity?.horse_id || buyerEntities.length === 0) {
      toast.error('No horse or buyers to update ownership for');
      return;
    }
    setUpdatingOwnership(true);
    try {
      // Mark existing current ownership records as historical
      const existingRecords = await api.get(`/ownership-records?horse_id=${opportunity.horse_id}&is_current=true`);
      for (const record of (existingRecords.data || [])) {
        await api.put(`/ownership-records/${record.id}`, {
          is_current: false,
          date_sold: new Date().toISOString()
        });
      }
      // Create new ownership records for each buyer using specified percentages
      for (let i = 0; i < buyerEntities.length; i++) {
        const buyer = buyerEntities[i];
        const pct = buyerEntities.length > 1 ? (buyerPercents[buyer.id] || 0) : 100;
        await api.post('/ownership-records', {
          horse_id: opportunity.horse_id,
          entity_type: buyer.crm_class || 'contact',
          entity_id: buyer.id,
          ownership_percent: pct,
          date_acquired: new Date().toISOString(),
          is_primary_owner: i === 0,
          is_current: true,
          purchase_price: opportunity.sale_price || null,
          notes: `Purchased at ${sale?.sale_name || 'sale'}`
        });
      }
      toast.success('Ownership records updated for ' + buyerEntities.map(b => b.system_name).join(', '));
      setOwnershipUpdated(true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update ownership records');
    }
    setUpdatingOwnership(false);
  };

  const isSold = opportunity?.result === 'sold';
  const isWithdrawn = opportunity?.result === 'withdrawn';
  const nonSoldTypes = ['invoice_generation', 'seller_thank_you', 'logistics'];
  const allNonSold = [...financial, ...handover].filter(d => nonSoldTypes.includes(d.type));
  const withdrawnDeliverables = [...financial, ...handover].filter(d => ['invoice_generation', 'logistics'].includes(d.type));

  return (
    <div className="space-y-6">
      {/* Buyer Card - only for Sold */}
      {isSold && (
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-semibold">Buyer(s)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {/* Show existing buyers */}
            {buyerEntities.length > 0 && (
              <div className="space-y-2 mb-3">
                {buyerEntities.map(buyer => (
                  <div key={buyer.id} className="flex items-center gap-3 p-2 border rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{buyer.system_name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {buyer.person_industry_role?.replace(/_/g, ' ') || buyer.organization_industry_role?.replace(/_/g, ' ') || buyer.crm_type}
                      </p>
                    </div>
                    {buyerEntities.length > 1 && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={buyerPercents[buyer.id] ?? ''}
                          onChange={(e) => updateBuyerPercent(buyer.id, e.target.value)}
                          className="w-16 h-7 text-xs text-center"
                          disabled={isReadOnly}
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                    {!isReadOnly && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 shrink-0" onClick={() => removeBuyer(buyer.id)}>Remove</Button>
                    )}
                  </div>
                ))}
                {buyerEntities.length > 1 && (
                  <p className={`text-xs ${Object.values(buyerPercents).reduce((a, b) => a + b, 0) === 100 ? 'text-green-600' : 'text-red-500'}`}>
                    Total: {Object.values(buyerPercents).reduce((a, b) => a + b, 0)}%{Object.values(buyerPercents).reduce((a, b) => a + b, 0) !== 100 ? ' (must equal 100%)' : ''}
                  </p>
                )}
              </div>
            )}
            {/* Search to add more buyers */}
            {!isReadOnly && !quickCreateBuyer && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Search for buyer..."
                    value={buyerSearch}
                    onChange={(e) => { setBuyerSearch(e.target.value); searchBuyer(e.target.value); }}
                    onFocus={() => setBuyerFocused(true)}
                    onBlur={() => setTimeout(() => setBuyerFocused(false), 200)}
                    disabled={isReadOnly}
                  />
                </div>
                {buyerFocused && buyerResults.length > 0 && (
                  <div className="border rounded max-h-32 overflow-y-auto">
                    {buyerResults.map(e => (
                      <div key={e.id} className="p-2 text-sm hover:bg-muted/50 cursor-pointer flex items-center justify-between" onClick={() => selectBuyer(e)}>
                        <span>{e.system_name}</span>
                        <span className="text-xs text-muted-foreground capitalize">{e.crm_type}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setQuickCreateBuyer(true)}>+ Create New Buyer</Button>
              </div>
            )}
            {!isReadOnly && quickCreateBuyer && (
              <div className="space-y-3 p-3 border rounded">
                <div className="flex gap-2">
                  <Button size="sm" variant={newBuyer.crm_type === 'person' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setNewBuyer(b => ({ ...b, crm_type: 'person' }))}>Person</Button>
                  <Button size="sm" variant={newBuyer.crm_type === 'organization' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setNewBuyer(b => ({ ...b, crm_type: 'organization' }))}>Organization</Button>
                </div>
                <div className="flex gap-2">
                  <Select value={newBuyer.crm_class} onValueChange={(v) => setNewBuyer(b => ({ ...b, crm_class: v }))}>
                    <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="account">Account</SelectItem>
                      <SelectItem value="contact">Contact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newBuyer.crm_type === 'person' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="First Name *" value={newBuyer.first_name} onChange={(e) => setNewBuyer(b => ({ ...b, first_name: e.target.value }))} className="h-8 text-sm" />
                    <Input placeholder="Last Name *" value={newBuyer.last_name} onChange={(e) => setNewBuyer(b => ({ ...b, last_name: e.target.value }))} className="h-8 text-sm" />
                  </div>
                ) : (
                  <Input placeholder="Organization Name *" value={newBuyer.org_name} onChange={(e) => setNewBuyer(b => ({ ...b, org_name: e.target.value }))} className="h-8 text-sm" />
                )}
                <Select value={newBuyer.industry_role} onValueChange={(v) => setNewBuyer(b => ({ ...b, industry_role: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Industry Role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="farm_manager">Farm Manager</SelectItem>
                    <SelectItem value="veterinarian">Veterinarian</SelectItem>
                    <SelectItem value="consignor">Consignor</SelectItem>
                    <SelectItem value="syndicate_member">Syndicate Member</SelectItem>
                    <SelectItem value="farm">Farm</SelectItem>
                    <SelectItem value="farm_boarding">Farm Boarding</SelectItem>
                    <SelectItem value="stable">Stable</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateBuyer} disabled={savingBuyer} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
                  <Button size="sm" variant="outline" onClick={() => setQuickCreateBuyer(false)} className="text-xs">Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Financial Deliverables & Settlement - only for Sold */}
      {isSold && (
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Financial Deliverables & Settlement</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          {financial.map(d => (
            <DeliverableRow
              key={d.id}
              deliverable={d}
              isReadOnly={isReadOnly}
              opportunity={opportunity}
              onUpdate={(data) => { updateDeliverable(d.id, data); }}
              onPreview={(file) => setPreviewFile(file)}
              onAction={d.type === 'invoice_generation' ? (d.metadata?.invoice_id ? () => navigate(`/invoices/${d.metadata.invoice_id}`) : handleGenerateInvoice) : null}
              actionLabel={d.type === 'invoice_generation' ? (d.metadata?.invoice_id ? 'View Invoice' : 'Generate Invoice') : null}
            />
          ))}
        </CardContent>
      </Card>
      )}

      {/* Change of Ownership Handover (Sold) / Post-Sale Actions (non-Sold) */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold">{isSold ? 'Change of Ownership Handover' : isWithdrawn ? 'Post-Sale Actions' : 'Post-Sale Actions'}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          {(isSold ? handover : isWithdrawn ? withdrawnDeliverables : allNonSold).map(d => (
            <DeliverableRow
              key={d.id}
              deliverable={d}
              isReadOnly={isReadOnly}
              opportunity={opportunity}
              onUpdate={(data) => { updateDeliverable(d.id, data); }}
              onPreview={(file) => setPreviewFile(file)}
              onAction={
                d.type === 'invoice_generation' ? (d.metadata?.invoice_id ? () => navigate(`/invoices/${d.metadata.invoice_id}`) : handleGenerateInvoice) :
                d.type === 'seller_thank_you'
                  ? () => setSellerThankYouDialog({ id: d.id, draftPath: `/post-sale/${d.id}/seller-thank-you-draft`, sendPath: `/post-sale/${d.id}/send-seller-thank-you-email` })
                  : d.type === 'buyer_thank_you'
                    ? () => setBuyerThankYouDialog({ id: d.id, draftPath: `/post-sale/${d.id}/buyer-thank-you-draft`, sendPath: `/post-sale/${d.id}/send-buyer-thank-you-email` })
                    : null
              }
              actionLabel={
                d.type === 'invoice_generation' ? (d.metadata?.invoice_id ? 'View Invoice' : 'Generate Invoice') :
                (d.type === 'buyer_thank_you' || d.type === 'seller_thank_you')
                  ? 'Create Email'
                  : null
              }
            />
          ))}
          {/* Update Ownership in Green Door */}
          {isSold && (
            <div className="flex items-center gap-3 p-3 border rounded hover:bg-muted/30 transition-colors">
              {ownershipUpdated ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-gray-400 shrink-0" />
              )}
              <p className="text-sm font-medium flex-1">Update Ownership in Green Door</p>
              {!ownershipUpdated && (
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-white text-xs shrink-0"
                  onClick={handleUpdateOwnership}
                  disabled={updatingOwnership || buyerEntities.length === 0 || !opportunity?.horse_id}
                  data-testid="update-ownership-btn"
                >
                  {updatingOwnership ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {updatingOwnership ? 'Updating...' : 'Update Ownership'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publish to Portal */}
      {isSold && (
        <div className="flex justify-end mt-4">
          <Button
            variant={opportunity?.published_to_portal ? "default" : "outline"}
            className={opportunity?.published_to_portal ? 'bg-gray-400 hover:bg-gray-400 text-white cursor-default' : ''}
            disabled={opportunity?.published_to_portal}
            onClick={async () => {
              try {
                await api.put(`/sale-opportunities/${opportunityId}`, { published_to_portal: !opportunity?.published_to_portal });
                toast.success(opportunity?.published_to_portal ? 'Unpublished from portal' : 'Published to owner portal as Final Sale Report');
                if (onRefresh) onRefresh();
              } catch { toast.error('Failed to update'); }
            }}
            data-testid="publish-postsale-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {opportunity?.published_to_portal ? 'Published to Portal' : 'Publish to Portal'}
          </Button>
        </div>
      )}

      {/* Invoice Preview Dialog */}
      {invoiceDialog && (
        <Dialog open={!!invoiceDialog} onOpenChange={(v) => { if (!v) setInvoiceDialog(null); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Invoice Generated</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Sale Company:</span> <span className="font-medium">{invoiceDialog.sale_company}</span></div>
                <div><span className="text-muted-foreground">Sale:</span> <span className="font-medium">{invoiceDialog.sale_name}</span></div>
                <div><span className="text-muted-foreground">Hip #:</span> <span className="font-medium">#{invoiceDialog.hip_number || 'TBD'}</span></div>
                <div><span className="text-muted-foreground">Horse:</span> <span className="font-medium">{invoiceDialog.horse_name}</span></div>
                <div><span className="text-muted-foreground">Result:</span> <span className="font-medium capitalize">{invoiceDialog.result}</span></div>
                <div><span className="text-muted-foreground">Currency:</span> <span className="font-medium">{invoiceDialog.currency}</span></div>
              </div>
              <div className="border-t pt-3">
                <p className="text-sm font-semibold mb-2">Line Items</p>
                <div className="space-y-1">
                  {invoiceDialog.line_items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className={item.amount < 0 ? 'text-muted-foreground' : ''}>{item.description}</span>
                      <span className={item.amount < 0 ? 'text-red-600' : 'font-medium'}>
                        {item.amount < 0 ? '-' : ''}${Math.abs(item.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm font-semibold border-t mt-2 pt-2">
                  <span>Net Proceeds</span>
                  <span className="text-primary">${invoiceDialog.net_proceeds?.toLocaleString()}</span>
                </div>
              </div>
              <Badge className="bg-yellow-100 text-yellow-700">Draft</Badge>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* PDF Preview Dialog */}
      {previewFile && (
        <Dialog open={!!previewFile} onOpenChange={(v) => { if (!v) setPreviewFile(null); }}>
          <DialogContent className="sm:max-w-5xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{previewFile.file_name || 'Document Preview'}</DialogTitle>
            </DialogHeader>
            <iframe
              src={`${window.location.origin}/api/${previewFile.file_url.replace(/^\//, '')}`}
              title="Document Preview"
              className="w-full h-[70vh] border-0 rounded"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewFile(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {sellerThankYouDialog && (
        <SellerThankYouEmailDialog
          open={!!sellerThankYouDialog}
          onClose={(sent) => { setSellerThankYouDialog(null); if (sent) invalidatePostSale(); }}
          deliverableId={sellerThankYouDialog.id}
          draftPath={sellerThankYouDialog.draftPath}
          sendPath={sellerThankYouDialog.sendPath}
        />
      )}
      {buyerThankYouDialog && (
        <BuyerThankYouEmailDialog
          open={!!buyerThankYouDialog}
          onClose={(sent) => { setBuyerThankYouDialog(null); if (sent) invalidatePostSale(); }}
          deliverableId={buyerThankYouDialog.id}
          draftPath={buyerThankYouDialog.draftPath}
          sendPath={buyerThankYouDialog.sendPath}
        />
      )}
    </div>
  );
};

const DeliverableRow = ({ deliverable, isReadOnly, onUpdate, onAction, actionLabel, opportunity, onPreview }) => {
  const [form, setForm] = useState({ status: deliverable.status, notes: deliverable.notes || '', metadata: deliverable.metadata || {} });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [quickCreate, setQuickCreate] = useState(false);
  const [newFarmName, setNewFarmName] = useState('');
  const [saving, setSaving] = useState(false);
  const isLogistics = deliverable.type === 'logistics';
  const hasUpload = deliverable.type === 'tax_export_declaration' || deliverable.type === 'insurance_certificate';

  useEffect(() => {
    setForm({ status: deliverable.status, notes: deliverable.notes || '', metadata: deliverable.metadata || {} });
  }, [deliverable]);

  useEffect(() => {
    if (dialogOpen && isLogistics) {
      api.get('/crm-entities').then(res => {
        const farms = (res.data || []).filter(e =>
          e.organization_industry_role === 'farm_boarding' || e.organization_industry_role === 'farm'
        );
        setFacilities(farms);
      }).catch(() => {});
    }
  // eslint-disable-next-line
  }, [dialogOpen, isLogistics]);

  const handleQuickCreateFarm = async () => {
    if (!newFarmName) { toast.error('Farm name required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/crm-entities', {
        crm_type: 'organization', crm_class: 'contact',
        organization_name: newFarmName, organization_industry_role: 'farm_boarding',
        system_name: newFarmName,
      });
      setFacilities(prev => [...prev, res.data]);
      setForm(f => ({ ...f, metadata: { ...f.metadata, transporting_to_id: res.data.id, transporting_to_name: res.data.system_name } }));
      setQuickCreate(false);
      setNewFarmName('');
      toast.success('Farm created');
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  // Use the same status icons/colors as the rest of the app (from saleConstants)
  const iconConfig = { Icon: saleStatusIcons[deliverable.status] || saleStatusIcons.not_started, color: saleStatusColors[deliverable.status] || saleStatusColors.not_started };
  const StatusIcon = iconConfig.Icon;

  const handleSave = () => {
    onUpdate(form);
    setDialogOpen(false);
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/post-sale/${deliverable.id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      const newStatus = (form.status === 'not_started' || form.status === 'pending') ? 'in_progress' : form.status;
      onUpdate({ status: newStatus, notes: form.notes, metadata: { ...form.metadata, file_name: res.data.file_name, file_url: res.data.file_url } });
      setDialogOpen(false);
    } catch { toast.error('Upload failed'); }
  };

  const transportName = deliverable.metadata?.transporting_to_name;

  return (
    <>
      <div className="flex items-center gap-3 p-3 border rounded hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => setDialogOpen(true)}>
        <StatusIcon className={`w-5 h-5 ${iconConfig.color} shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{deliverable.label}</p>
          {transportName && <p className="text-xs text-muted-foreground">Transporting to: {transportName}</p>}
          {deliverable.metadata?.file_name && <p className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" />{deliverable.metadata.file_name}</p>}
          {deliverable.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">{deliverable.notes}</p>}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{deliverable.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{deliverable.description}</p>

            {/* Logistics: Transporting To */}
            {isLogistics && (
              <div className="space-y-2">
                <Label>Transporting To</Label>
                {form.metadata?.transporting_to_name && !quickCreate ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{form.metadata.transporting_to_name}</Badge>
                    <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setForm(f => ({ ...f, metadata: { ...f.metadata, transporting_to_id: null, transporting_to_name: null } }))}>Change</Button>
                  </div>
                ) : !quickCreate ? (
                  <div className="space-y-1">
                    <Select value={form.metadata?.transporting_to_id || ''} onValueChange={(v) => {
                      const farm = facilities.find(f => f.id === v);
                      setForm(f => ({ ...f, metadata: { ...f.metadata, transporting_to_id: v, transporting_to_name: farm?.system_name || '' } }));
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select farm..." /></SelectTrigger>
                      <SelectContent>
                        {facilities.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.system_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setQuickCreate(true)}>+ Create New Farm</Button>
                  </div>
                ) : (
                  <div className="space-y-2 p-2 border rounded">
                    <Input placeholder="Farm Name *" value={newFarmName} onChange={(e) => setNewFarmName(e.target.value)} className="text-sm" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleQuickCreateFarm} disabled={saving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
                      <Button size="sm" variant="outline" onClick={() => setQuickCreate(false)} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={isReadOnly}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="not_applicable">N/A</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Document Upload for Export Declaration and Certificate of Sale */}
            {hasUpload && (
              <div className="space-y-2">
                <Label>Document</Label>
                {deliverable.metadata?.file_name ? (
                  <div className="flex items-center gap-2 p-3 border rounded bg-muted/30">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm flex-1 cursor-pointer hover:underline text-primary" onClick={(e) => { e.stopPropagation(); setDialogOpen(false); onPreview({ file_name: deliverable.metadata.file_name, file_url: deliverable.metadata.file_url }); }}>{deliverable.metadata.file_name}</span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setDialogOpen(false); onPreview({ file_name: deliverable.metadata.file_name, file_url: deliverable.metadata.file_url }); }} title="Preview">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                      document.getElementById(`upload-${deliverable.id}`)?.click();
                    }}>Replace</Button>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed rounded p-6 text-center cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => document.getElementById(`upload-${deliverable.id}`)?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-muted/50'); }}
                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-muted/50'); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-muted/50');
                      const file = e.dataTransfer.files?.[0];
                      if (file) await handleFileUpload(file);
                    }}
                  >
                    <p className="text-sm text-muted-foreground">Drag & drop a file here, or click to browse</p>
                  </div>
                )}
                <input id={`upload-${deliverable.id}`} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await handleFileUpload(file);
                  e.target.value = '';
                }} />
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Add notes..." disabled={isReadOnly} />
            </div>

            {/* Seller Thank You: show send history when emailed, or Create Email button */}
            {deliverable.type === 'seller_thank_you' && deliverable.metadata?.emailed ? (
              <EmailSendHistory
                sends={
                  deliverable.metadata?.email_sends?.length > 0
                    ? deliverable.metadata.email_sends
                    : [{ sent_at: deliverable.metadata.emailed_at, sent_by: deliverable.metadata.emailed_by, sent_to: deliverable.metadata.emailed_to, eml_file_id: deliverable.metadata.emailed_eml_file_id }]
                }
                onResend={() => { onAction(); setDialogOpen(false); }}
                emlFilename="seller_thank_you.eml"
                layout="stacked"
              />
            ) : deliverable.type === 'buyer_thank_you' && deliverable.metadata?.emailed ? (
              <EmailSendHistory
                sends={
                  deliverable.metadata?.email_sends?.length > 0
                    ? deliverable.metadata.email_sends
                    : [{ sent_at: deliverable.metadata.emailed_at, sent_by: deliverable.metadata.emailed_by, sent_to: deliverable.metadata.emailed_to, eml_file_id: deliverable.metadata.emailed_eml_file_id }]
                }
                onResend={() => { onAction(); setDialogOpen(false); }}
                emlFilename="buyer_thank_you.eml"
                layout="stacked"
              />
            ) : (
              onAction && (
                <Button variant="outline" className="w-full" onClick={() => { onAction(); setDialogOpen(false); }}>{actionLabel}</Button>
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            {!isReadOnly && (
              <Button onClick={handleSave} className="bg-primary hover:bg-primary/90 text-white">Save</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostSaleTab;
